import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Tab,
  Tabs,
} from '@mui/material';
import { storage, auth } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// Import the compatibility version of Firebase (will use in the direct test)
declare const firebase: any;

const StorageTester: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadURL, setDownloadURL] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customPath, setCustomPath] = useState('test-uploads/test-file.jpg');
  const [activeTab, setActiveTab] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const directFileInputRef = useRef<HTMLInputElement>(null);
  const logAreaRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Load Firebase SDK for the direct test
    const loadFirebaseSDK = async () => {
      if (typeof firebase === 'undefined') {
        const script1 = document.createElement('script');
        script1.src = 'https://www.gstatic.com/firebasejs/9.6.1/firebase-app-compat.js';
        script1.async = true;
        
        const script2 = document.createElement('script');
        script2.src = 'https://www.gstatic.com/firebasejs/9.6.1/firebase-storage-compat.js';
        script2.async = true;
        
        const script3 = document.createElement('script');
        script3.src = 'https://www.gstatic.com/firebasejs/9.6.1/firebase-auth-compat.js';
        script3.async = true;
        
        document.body.appendChild(script1);
        document.body.appendChild(script2);
        document.body.appendChild(script3);
        
        addDirectLog('Loading Firebase SDK...');
      } else {
        addDirectLog('Firebase SDK already loaded.');
      }
    };
    
    loadFirebaseSDK();
  }, []);
  
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `${timestamp}: ${message}`]);
    console.log(`${timestamp}: ${message}`);
  };
  
  // Special logging function for the direct test
  const addDirectLog = (message: string) => {
    if (!logAreaRef.current) return;
    
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = document.createElement('div');
    logEntry.className = 'log-entry';
    logEntry.style.margin = '5px 0';
    logEntry.style.fontFamily = 'monospace';
    logEntry.textContent = `${timestamp}: ${message}`;
    
    logAreaRef.current.appendChild(logEntry);
    logAreaRef.current.scrollTop = logAreaRef.current.scrollHeight;
    console.log(`${timestamp}: ${message}`);
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);
      addLog(`File selected: ${selectedFile.name} (${Math.round(selectedFile.size / 1024)}KB)`);
    }
  };
  
  const testStorageConnection = () => {
    try {
      addLog("Testing basic storage configuration...");
      
      if (!storage) {
        addLog("ERROR: Storage object is not initialized!");
        setError("Storage object is not initialized!");
        return;
      }
      
      // Check if the storage bucket is configured
      const bucket = storage.app.options.storageBucket;
      if (!bucket) {
        addLog("ERROR: Storage bucket is not configured!");
        setError("Storage bucket is not configured!");
        return;
      }
      
      addLog(`Storage initialized with bucket: ${bucket}`);
      
      // Check if user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        addLog("WARNING: No authenticated user found. This may cause permission issues.");
      } else {
        addLog(`Authenticated as: ${currentUser.uid} (${currentUser.email})`);
      }
      
      addLog("Basic storage configuration looks OK");
    } catch (err: any) {
      addLog(`ERROR during connection test: ${err.message}`);
      setError(`Connection test failed: ${err.message}`);
    }
  };
  
  const clearLogs = () => {
    setLogs([]);
    setError(null);
  };
  
  const testUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }
    
    setError(null);
    setIsUploading(true);
    setUploadProgress(0);
    setDownloadURL(null);
    
    try {
      addLog("Starting upload test...");
      
      // Check storage connection first
      if (!storage) {
        throw new Error("Storage is not initialized");
      }
      
      // Check authentication
      const currentUser = auth.currentUser;
      if (!currentUser) {
        addLog("WARNING: Not authenticated. Attempting upload anyway...");
      } else {
        addLog(`Uploading as user: ${currentUser.uid}`);
        try {
          const token = await currentUser.getIdToken(true);
          addLog("Auth token refreshed successfully");
        } catch (tokenError: any) {
          addLog(`WARNING: Could not refresh token: ${tokenError.message}`);
        }
      }
      
      // Create a unique path
      const timestamp = new Date().getTime();
      const path = customPath || `test-uploads/test-${timestamp}.jpg`;
      
      addLog(`Creating storage reference at path: ${path}`);
      const storageRef = ref(storage, path);
      
      addLog("Setting up upload task...");
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      // Monitor the upload task
      uploadTask.on(
        'state_changed',
        // Progress handler
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
          addLog(`Upload progress: ${progress.toFixed(2)}% (${snapshot.state})`);
        },
        // Error handler
        (error) => {
          addLog(`UPLOAD ERROR: ${error.message}`);
          addLog(`Error code: ${error.code}`);
          // Log additional error details if available
          if ('serverResponse' in error) {
            addLog(`Server response: ${(error as any).serverResponse}`);
          }
          setError(`Upload failed: ${error.message}`);
          setIsUploading(false);
        },
        // Completion handler
        async () => {
          addLog("Upload completed! Getting download URL...");
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setDownloadURL(url);
            addLog(`Download URL: ${url}`);
            addLog("Test completed successfully!");
          } catch (urlError: any) {
            addLog(`ERROR getting URL: ${urlError.message}`);
            setError(`Upload succeeded but couldn't get URL: ${urlError.message}`);
          }
          setIsUploading(false);
        }
      );
    } catch (err: any) {
      addLog(`CRITICAL ERROR: ${err.message}`);
      setError(`Test failed: ${err.message}`);
      setIsUploading(false);
    }
  };
  
  // Function to handle direct upload using the compatibility version
  const handleDirectUpload = async () => {
    if (!directFileInputRef.current?.files?.length) {
      addDirectLog('Please select a file first');
      return;
    }
    
    // Clear progress bar
    if (progressBarRef.current) {
      progressBarRef.current.style.width = '0%';
    }
    
    const file = directFileInputRef.current.files[0];
    addDirectLog(`Selected file: ${file.name} (${Math.round(file.size / 1024)}KB)`);
    
    // Initialize Firebase if not already done
    if (typeof firebase === 'undefined') {
      addDirectLog('ERROR: Firebase SDK not loaded. Please wait a moment and try again.');
      return;
    }
    
    // Initialize Firebase if not already done
    if (!firebase.apps || !firebase.apps.length) {
      addDirectLog('Initializing Firebase...');
      firebase.initializeApp({
        apiKey: "AIzaSyC2opn2EgaS8mhB9-CxgwJiCuHvu7DQ5Z0",
        authDomain: "superior-payroll-cw.firebaseapp.com",
        projectId: "superior-payroll-cw",
        storageBucket: "superior-payroll-cw.firebasestorage.app",
        messagingSenderId: "520825490567",
        appId: "1:520825490567:web:724a1e86927fafae457c22",
        measurementId: "G-B4V9XXM89R"
      });
    }
    
    // Use the existing authenticated user instead of trying anonymous sign-in
    const currentUser = auth.currentUser;
    
    // Check if the user is signed in
    if (!currentUser) {
      addDirectLog('ERROR: You must be signed in to upload files. Please log in and try again.');
      return;
    }
    
    addDirectLog(`Using existing authentication. Signed in as: ${currentUser.email || currentUser.uid}`);
    
    try {
      // Get a fresh token to ensure we have valid authentication
      const token = await currentUser.getIdToken(true);
      addDirectLog('Authentication token refreshed successfully');
      
      // Since we're using the compat API for the direct test but modular API elsewhere,
      // we need to ensure the Firebase Auth instances are synced
      await firebase.auth().updateCurrentUser(auth.currentUser);
      addDirectLog('Firebase auth sync completed');
    } catch (error: any) {
      addDirectLog(`Authentication error: ${error.code} - ${error.message}`);
      // Continue anyway, as the current token might still be valid
    }
    
    // Reference to storage
    const storage = firebase.storage();
    addDirectLog(`Storage bucket: ${storage.app.options.storageBucket}`);
    
    // Create a storage reference
    const timestamp = new Date().getTime();
    const storageRef = storage.ref(`direct-test/${timestamp}-${file.name}`);
    addDirectLog(`Storage reference created at: direct-test/${timestamp}-${file.name}`);
    
    // Upload the file
    addDirectLog('Starting upload...');
    addDirectLog('WARNING: If upload seems stuck, check browser console for CORS errors');
    
    const metadata = {
      contentType: file.type,
      customMetadata: {
        uploadedBy: currentUser.uid,
        uploadTime: new Date().toISOString()
      }
    };
    
    const uploadTask = storageRef.put(file, metadata);
    
    // Monitor upload progress
    uploadTask.on('state_changed', 
      // Progress function
      (snapshot: any) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (progressBarRef.current) {
          progressBarRef.current.style.width = `${progress}%`;
        }
        addDirectLog(`Upload progress: ${progress.toFixed(2)}% (${snapshot.state})`);
      },
      // Error function
      (error: any) => {
        addDirectLog(`Upload error: ${error.code} - ${error.message}`);
        if (error.serverResponse) {
          addDirectLog(`Server response: ${error.serverResponse}`);
        }
        console.error('Full error object:', error);
      },
      // Complete function
      async () => {
        addDirectLog('Upload completed!');
        try {
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
          addDirectLog(`Download URL: ${downloadURL}`);
          
          // Create clickable link
          const linkElement = document.createElement('a');
          linkElement.href = downloadURL;
          linkElement.textContent = 'View uploaded file';
          linkElement.target = '_blank';
          linkElement.style.color = '#1976d2';
          linkElement.style.textDecoration = 'underline';
          
          const linkContainer = document.createElement('div');
          linkContainer.className = 'log-entry';
          linkContainer.style.margin = '5px 0';
          linkContainer.style.fontFamily = 'monospace';
          
          linkContainer.appendChild(linkElement);
          
          if (logAreaRef.current) {
            logAreaRef.current.appendChild(linkContainer);
          }
        } catch (error: any) {
          addDirectLog(`Error getting download URL: ${error.message}`);
        }
      }
    );
  };
  
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Firebase Storage Tester
      </Typography>
      
      <Typography variant="body1" paragraph>
        This tool helps diagnose Firebase Storage issues by directly testing uploads.
      </Typography>
      
      <Tabs 
        value={activeTab} 
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="React Upload Test" />
        <Tab label="Direct Upload Test" />
      </Tabs>
      
      {activeTab === 0 ? (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {downloadURL && (
            <Alert severity="success" sx={{ mb: 2 }}>
              File uploaded successfully! <a href={downloadURL} target="_blank" rel="noopener noreferrer">View file</a>
            </Alert>
          )}
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              1. Test Storage Connection
            </Typography>
            
            <Button 
              variant="outlined" 
              onClick={testStorageConnection}
              sx={{ mr: 1 }}
            >
              Test Connection
            </Button>
            
            <Button 
              variant="text" 
              color="secondary"
              onClick={clearLogs}
            >
              Clear Logs
            </Button>
          </Paper>
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              2. Upload Test
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Custom Storage Path"
                value={customPath}
                onChange={(e) => setCustomPath(e.target.value)}
                fullWidth
                margin="normal"
                helperText="Customize the storage path (e.g., 'test/myfile.jpg')"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button
                variant="contained"
                onClick={() => fileInputRef.current?.click()}
                sx={{ mr: 2 }}
              >
                Select File
              </Button>
              
              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              
              {file && (
                <Typography variant="body2">
                  {file.name} ({Math.round(file.size / 1024)}KB)
                </Typography>
              )}
            </Box>
            
            <Button
              variant="contained"
              color="primary"
              onClick={testUpload}
              disabled={!file || isUploading}
              startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {isUploading ? `Uploading (${Math.round(uploadProgress)}%)` : 'Test Upload'}
            </Button>
          </Paper>
          
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Logs
            </Typography>
            
            <Divider sx={{ mb: 1 }} />
            
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {logs.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No logs yet. Start a test to see results." />
                  </ListItem>
                ) : (
                  logs.map((log, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={log} 
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          style: { 
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          } 
                        }} 
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Box>
          </Paper>
        </>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Direct Firebase Storage Test
          </Typography>
          <Typography variant="body2" paragraph>
            This test bypasses React and uses the Firebase JS SDK directly, similar to how it would work in a plain HTML page.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <input 
              type="file" 
              ref={directFileInputRef} 
              style={{ marginBottom: '10px' }}
            />
            
            <div 
              style={{ 
                width: '100%', 
                backgroundColor: '#ddd', 
                borderRadius: '4px', 
                marginBottom: '10px' 
              }}
            >
              <div 
                ref={progressBarRef} 
                style={{ 
                  height: '20px', 
                  backgroundColor: '#4CAF50', 
                  borderRadius: '4px', 
                  width: '0%', 
                  transition: 'width 0.3s' 
                }}
              ></div>
            </div>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleDirectUpload}
            >
              Direct Upload
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            Direct Test Logs:
          </Typography>
          
          <div 
            ref={logAreaRef} 
            style={{ 
              marginTop: '10px', 
              padding: '10px', 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              height: '300px', 
              overflowY: 'auto' 
            }}
          >
            <div className="log-entry" style={{ margin: '5px 0', fontFamily: 'monospace' }}>
              Direct test initialized. Select a file and click "Direct Upload".
            </div>
          </div>
        </Paper>
      )}
    </Box>
  );
};

export default StorageTester; 